import { BagIcon, Button } from '@iheartjane/reefer';
import { pluralize } from '@iheartjane/utils';
import { useEffect } from 'react';
import { useFetcher } from 'react-router';

import { BagTransferModal } from '@jane/bloom/components/bagTransferModal/bagTransferModal';
import { BloomLink } from '@jane/bloom/components/linkWithBasename';
import { useBagTransfer } from '@jane/bloom/hooks/cart/useBagTransfer';
import { useNavigateWithBasename } from '@jane/bloom/hooks/navigation/useNavigateWithBasename';
import { useBloomBasename } from '@jane/bloom/hooks/useBloomBasename';
import { useCheckoutLink } from '@jane/bloom/hooks/useCheckoutLink';
import { useRootLoaderData } from '@jane/bloom/hooks/useRootLoaderData';
import { trackHeaderTap } from '@jane/bloom/services/tracking/track';

import styles from './cartInfo.module.css';

type CartInfoParams = {
  count: number;
  disabled?: boolean;
};

export function CartInfo({ count, disabled }: CartInfoParams) {
  const { cart, featureFlags, promoCode, shoppingPreferences, stores } =
    useRootLoaderData();
  const checkoutUrl = useCheckoutLink({ includeBasename: false, promoCode });
  const navigate = useNavigateWithBasename();
  const basename = useBloomBasename();
  const replaceCartFetcher = useFetcher({ key: 'replace-cart-checkout' });
  const {
    cancelTransfer,
    checkAndHandleTransfer,
    confirmTransfer,
    newCart,
    showWarningModal,
  } = useBagTransfer();

  const oldStoreName = stores.find((store) => store.id === cart.storeId)?.name;
  const hasProductsAtNewStore =
    newCart && newCart.cart && newCart.cart.products.length > 0;

  // Watch for cart replacement to complete, then navigate
  useEffect(() => {
    if (replaceCartFetcher.state === 'idle' && replaceCartFetcher.data) {
      navigate(checkoutUrl);
    }
  }, [
    replaceCartFetcher.state,
    replaceCartFetcher.data,
    navigate,
    checkoutUrl,
  ]);

  const handleConfirm = () => {
    if (hasProductsAtNewStore) {
      confirmTransfer(() => {
        // Submit to replace the cart with the new cart
        replaceCartFetcher.submit(
          { shouldReplaceCart: 'true' },
          {
            action: `${basename}api/add-to-cart`,
            method: 'post',
          }
        );
      });
    } else {
      // No products at new store, so keep the old cart and navigate
      cancelTransfer(() => {
        navigate(checkoutUrl);
      });
    }
  };

  const handleCancel = () => {
    if (hasProductsAtNewStore) {
      cancelTransfer(() => {
        navigate(checkoutUrl);
      });
    } else {
      cancelTransfer();
    }
  };

  return (
    <>
      <BagTransferModal
        open={showWarningModal}
        variant="checkout"
        oldStoreName={oldStoreName}
        onConfirm={handleConfirm}
        onRequestClose={handleCancel}
        newBag={newCart}
      />
      <BloomLink
        aria-label="Check out"
        to={checkoutUrl}
        onClick={(e) => {
          if (disabled) {
            e.preventDefault();
            return;
          }

          trackHeaderTap(
            {
              reservationMode: shoppingPreferences?.reservationMode,
              tappable: 'bag icon',
            },
            featureFlags
          );

          const needsTransfer = checkAndHandleTransfer();
          if (needsTransfer) {
            e.preventDefault();
            return;
          }
        }}
        className={styles['cartLink']}
        reloadDocument
      >
        <Button.Icon
          icon={<BagIcon />}
          type="button"
          variant="minimal"
          className={styles['bagIcon']}
        />
        {count > 0 && (
          <div
            aria-label={`${pluralize({ noun: 'item', number: count })} in bag`}
            className={styles['cartCount']}
          >
            {count}
          </div>
        )}
      </BloomLink>
    </>
  );
}
